import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, onUnmounted, ref } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { useOddsToken } from '@/core/oddsApi/composable/useOddsToken';
import { useSelectionSubscription, useSelectionV2Subscription } from '@/core/oddsApi/oddsApiType';
import type { LeagueSize } from '@/interface/enum';
import { SiteStyle, SportType } from '@/interface/enum';
import { useToggleStore } from '@/store/toggleStore';

export function useSelectionSubscriptionCallback({
    marketId,
    selectionId,
    sportType,
    leagueSize,
    isSimulation,
    leagueId,
    isActive = ref(true),
}: {
    marketId: Ref<number>;
    selectionId: Ref<number>;
    sportType: SportType;
    leagueSize: LeagueSize;
    isSimulation: boolean;
    leagueId: number;
    isActive?: Ref<boolean>;
}) {
    const { isDynamicMarketMTokenEnabled } = storeToRefs(useToggleStore(SiteStyle.Asia));
    const { getOddsToken } = useOddsToken();

    const oldVariables = deepCompareComputed(() => ({
        query: {
            marketId: marketId.value,
            selectionId: selectionId.value,
        },
    }));

    const oldEnabled = computed(() => isActive.value && !isDynamicMarketMTokenEnabled.value);

    const oldSubscription = useSelectionSubscription(oldVariables, deepCompareComputed(() => ({ enabled: oldEnabled.value })));

    const variables = deepCompareComputed(() => {
        const oddsToken = getOddsToken(sportType, leagueSize, isSimulation);

        return {
            query: {
                marketId: marketId.value,
                selectionId: selectionId.value,
                oddsToken,
                sportType: SportType.toOddsApi(sportType),
                leagueId,
            },
        };
    });

    const enabled = computed(() => isActive.value && isDynamicMarketMTokenEnabled.value && !!variables.value.query.oddsToken);

    const subscription = useSelectionV2Subscription(variables, deepCompareComputed(() => ({ enabled: enabled.value })));

    type UpdatedFn = () => void;
    let updatedFn: UpdatedFn[] = [];
    const onUpdated = (callback: UpdatedFn) => {
        updatedFn.push(callback);
        onUnmounted(() => {
            updatedFn = updatedFn.filter(fn => fn !== callback);
        });
    };

    type DeletedFn = () => void;
    let deletedFn: DeletedFn[] = [];
    const onDeleted = (callback: DeletedFn) => {
        deletedFn.push(callback);
        onUnmounted(() => {
            deletedFn = deletedFn.filter(fn => fn !== callback);
        });
    };

    oldSubscription.onSubscription((payload) => {
        if (payload === undefined) return;
        if (payload === null) {
            deletedFn.forEach(fn => fn());
            return;
        }
        updatedFn.forEach(fn => fn());
    });

    subscription.onSubscription((payload) => {
        if (payload === undefined) return;
        if (payload === null) {
            deletedFn.forEach(fn => fn());
            return;
        }
        updatedFn.forEach(fn => fn());
    });

    return {
        onUpdated,
        onDeleted,
    };
}
